import { PollOption, PollState, VotedPoll } from './Poll.types';

const getVotedPollsKey = (userId?: string): string =>
  userId ? `naf_voted_polls_${userId}` : 'naf_voted_polls_anonymous';

export const getVotedPolls = (userId?: string): Record<string, VotedPoll> => {
  const key = getVotedPollsKey(userId);
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : {};
};

export const saveVotedPoll = (pollId: string, optionId: string, userId?: string): void => {
  const key = getVotedPollsKey(userId);
  const votedPolls = getVotedPolls(userId);
  votedPolls[pollId] = { optionId };
  localStorage.setItem(key, JSON.stringify(votedPolls));
};

export const calculatePercentage = (
  state: PollState,
  votes: number,
  totalVotes: number,
  options: PollOption[],
  optionId: string,
): number => {
  // Only add +1 for optimistic votes (fresh votes before server updates)
  // Don't add +1 for persisted votes (loaded from localStorage after refresh, server already has the vote)
  const shouldAddOptimisticVote = state.hasVoted && state.isOptimisticVote;
  const adjustedVotes = shouldAddOptimisticVote && state.votedOptionId === optionId ? votes + 1 : votes;

  let adjustedTotal = 0;
  if (shouldAddOptimisticVote && totalVotes === 0) {
    adjustedTotal = options.reduce((sum, opt) => sum + (opt.votes || 0), 0) + 1;
  } else if (totalVotes > 0) {
    adjustedTotal = totalVotes + (shouldAddOptimisticVote ? 1 : 0);
  } else {
    adjustedTotal = options.reduce((sum, opt) => sum + (opt.votes || 0), 0);
  }

  if (adjustedTotal === 0) return 0;
  return Math.round((adjustedVotes / adjustedTotal) * 100);
};

export const calculateDisplayTotalVotes = (state: PollState, totalVotes: number, options: PollOption[]): number => {
  // Only add +1 for optimistic votes (fresh votes before server updates)
  // Don't add +1 for persisted votes (loaded from localStorage after refresh, server already has the vote)
  const shouldAddOptimisticVote = state.hasVoted && state.isOptimisticVote;

  if (shouldAddOptimisticVote && totalVotes > 0) {
    return totalVotes + 1;
  }
  const sumOfVotes = options.reduce((sum, opt) => sum + (opt.votes || 0), 0);
  return shouldAddOptimisticVote ? sumOfVotes + 1 : sumOfVotes;
};
